import {
  type AddAliasRequest,
  API_ROUTES,
  type CreateVendorRequest,
  type ListVendorsQuery,
  type MergeVendorsRequest,
  type SetTaxInfoRequest,
  type UpdateVendorRequest,
  vendorDetailSchema,
  vendorSummarySchema,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** Supplier queries. */

export const vendorKeys = {
  all: (organizationId: string) => ['vendors', organizationId] as const,
  detail: (organizationId: string, vendorId: string) =>
    ['vendors', organizationId, vendorId] as const,
} as const;

const pageSchema = z.object({
  items: z.array(vendorSummarySchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

export const vendorsQuery = (organizationId: string, filters: Partial<ListVendorsQuery> = {}) =>
  queryOptions({
    queryKey: ['vendors', organizationId, 'list', filters] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== '') params.set(key, String(value));
      }
      const search = params.toString();
      return apiClient.request({
        path: `${API_ROUTES.vendors.list(organizationId)}${search === '' ? '' : `?${search}`}`,
        schema: pageSchema,
        signal,
      });
    },
    staleTime: 30_000,
  });

export const vendorQuery = (organizationId: string, vendorId: string) =>
  queryOptions({
    queryKey: vendorKeys.detail(organizationId, vendorId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.vendors.byId(organizationId, vendorId),
        schema: vendorDetailSchema,
        signal,
      }),
  });

export const createVendor = (organizationId: string, body: CreateVendorRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.vendors.create(organizationId),
    schema: vendorDetailSchema,
    body,
  });

export const updateVendor = (organizationId: string, vendorId: string, body: UpdateVendorRequest) =>
  apiClient.request({
    method: 'PATCH',
    path: API_ROUTES.vendors.byId(organizationId, vendorId),
    schema: vendorDetailSchema,
    body,
  });

export const removeVendorAlias = (organizationId: string, vendorId: string, aliasId: string) =>
  apiClient.request({
    path: API_ROUTES.vendors.alias(organizationId, vendorId, aliasId),
    method: 'DELETE',
    schema: vendorDetailSchema,
  });

export const addVendorAlias = (organizationId: string, vendorId: string, body: AddAliasRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.vendors.aliases(organizationId, vendorId),
    schema: vendorDetailSchema,
    body,
  });

export const setVendorTaxInfo = (
  organizationId: string,
  vendorId: string,
  body: SetTaxInfoRequest,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.vendors.taxInformation(organizationId, vendorId),
    schema: vendorDetailSchema,
    body,
  });

export const verifyVendorTaxInfo = (
  organizationId: string,
  vendorId: string,
  taxInfoId: string,
  note: string,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.vendors.verifyTaxInformation(organizationId, vendorId, taxInfoId),
    schema: vendorDetailSchema,
    body: { note },
  });

export const mergeVendors = (organizationId: string, targetId: string, body: MergeVendorsRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.vendors.merge(organizationId, targetId),
    schema: z.object({ moved: z.number().int() }),
    body,
  });
